import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from '@/lib/constants/engagement-codes'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'

// Base schema without conditional validation
const baseBillingSetupFormSchema = z.object({
  billingType: z.string().min(1, 'Field is required'),
  totalAmount: z.number().nullable(),
  totalInternalBudget: z.number().nullable(),
  selectedCurrency: z.string().min(1, 'Field is required'),
  selectedSalesTax: z.string().min(1, 'Field is required'),
  // Whether sales tax is charged on this engagement's billing. null = not explicitly set yet →
  // inherit from the client's OrganizationClientRole.IsChargeSalesTax (resolved on client select /
  // load); once set it is the engagement's own value and is independently editable. (GW-562)
  chargeSalesTax: z.boolean().nullable(),
})

// Function to create schema with conditional validation based on engagement type and whether accounting fields are required
export const createBillingSetupFormSchema = (engagementType: string, requireAccountingFields: boolean = false) => {
  return baseBillingSetupFormSchema.superRefine((data, ctx) => {
    const { billingType } = data
    
    // Time-based billing validations.
    // Don't validate totalAmount or totalInternalBudget if milestone billing type, since those are calculated from milestones.
    if (billingType === ENGAGEMENT_BILLING_TYPE.TIME_BASED) {
      // Only validate totalAmount for non-internal engagements
      if (engagementType !== ENGAGEMENT_TYPE.INTERNAL) {
        zodValidationHelpers.conditionalRequiredDecimal(
          ctx,
          ['totalAmount'],
          data.totalAmount,
          requireAccountingFields,
          'Value must be greater than zero'
        )
      }
      
      // Validate totalInternalBudget - required for advisory in accounting draft, optional for internal always
      // Always validate to ensure positive values when provided
      zodValidationHelpers.conditionalRequiredDecimal(
        ctx,
        ['totalInternalBudget'],
        data.totalInternalBudget,
        engagementType !== ENGAGEMENT_TYPE.INTERNAL && requireAccountingFields,
        'Amount must be greater than zero'
      )
    }
  })
}

// Inferred TypeScript type
export type BillingSetupFormData = Expand<z.infer<typeof baseBillingSetupFormSchema>>

